import { useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { resources } from "@constants/hardcoded-strings";
import { POLICY_CATEGORY_OPTIONS } from "@constants/policy-condition-category-options";
import { POLICY_COMPONENT_CREATION_TYPE_OPTIONS } from "@constants/policy-component-creation-type-options";
import { InputFormGroup } from "@containers/common/input-form-group/input-form-group";
import { SelectFormGroup } from "@containers/common/select-form-group/select-form-group";
import { ToggleFormGroup } from "@containers/common/toggle-form-group/toggle-form-group";
import { PolicyComponentCreationModeTypes } from "@enums/policy-component-creation-mode-types";
import { EntityStatusTypes } from "@enums/entity-status-types";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { SelectOption } from "@iddataweb/idw-common-ui/types";
import { CreatePolicyComponentGeneralStepValidationErrors } from "@idw-types/common/validation/create-policy-component/create-policy-component-validation-error";
import { Company, DeployedPolicyComponent, SimpleDeployedPolicyComponentCondition } from "@idw-types/data/reference-data/generic";
import { FormattedPolicyComponent, FormattedPolicyComponentCondition } from "@idw-types/data/reference-data/generic/policy-component";
import { getTriggerType } from "@utils/policies/policy-component-util";
import { sortStrings } from "@utils/string-util";
import styles from "./create-policy-component-general-step-container.less";


type CreatePolicyComponentGeneralStepContainerProps = {
    policyComponents: Array<DeployedPolicyComponent>;
    policyComponentConditions: Array<SimpleDeployedPolicyComponentCondition>;
    companies: Array<Company>;
    policyComponentState: FormattedPolicyComponent;
    validationErrors: CreatePolicyComponentGeneralStepValidationErrors;
    policyComponentCreationMode: PolicyComponentCreationModeTypes;
    selectedPolicyComponent: DeployedPolicyComponent;
    setPolicyComponentState: (policyComponentState: FormattedPolicyComponent) => void;
    setSelectedPolicyComponent: (selectedPolicyComponent: DeployedPolicyComponent) => void;
    setPolicyComponentCreationMode: (policyComponentCreationMode: PolicyComponentCreationModeTypes) => void;
}

export const CreatePolicyComponentGeneralStepContainer = (props: CreatePolicyComponentGeneralStepContainerProps) => {
    const {
        policyComponents,
        policyComponentConditions,
        companies,
        policyComponentState,
        validationErrors,
        policyComponentCreationMode,
        selectedPolicyComponent,
        setPolicyComponentState,
        setSelectedPolicyComponent,
        setPolicyComponentCreationMode
    } = props;

    const [labelWidth] = useState(LabelWidthTypes.MEDIUM);
    const [layout] = useState(LayoutTypes.ROW);
    const createPolicyComponentResource = resources.modals.createPolicyComponent;

    const handlePolicyComponentTypeChange = (selectedOption: SelectOption<PolicyComponentCreationModeTypes>) => {
        setPolicyComponentCreationMode(selectedOption.value);

        setPolicyComponentState({
            ...policyComponentState,
            conditions: []
        });
    };

    const handleOrganizationChange = (selectedOption: SelectOption<string>) => {
        setPolicyComponentState({
            ...policyComponentState,
            organizationID: selectedOption?.value || null
        });
    };

    const handlePolicyComponentListChange = (selectedOption: SelectOption<string>) => {
        const policyComponent = policyComponents.find(component => component.id === selectedOption.value);
        setSelectedPolicyComponent(policyComponent);

        const conditions: Array<FormattedPolicyComponentCondition> = policyComponentConditions
            .filter(pcc => pcc.policyComponentID === policyComponent.id)
            .map(pcc => {
                const newCondition: FormattedPolicyComponentCondition = {
                    ...pcc,
                    id: null,
                    attributeTypeID: null, //maybe need to fix this
                    policyComponentID: null,
                    uuid: uuidv4(),
                    triggerType: getTriggerType(pcc)
                };
                return newCondition;
            });

        setPolicyComponentState({
            conditions: conditions,
            ...policyComponent,
            id: null,
            name: `${policyComponent.name} copy`,
            organizationID: policyComponentState.organizationID ? policyComponentState.organizationID : null
        });
    };

    const handlePolicyComponentNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setPolicyComponentState({
            ...policyComponentState,
            name: event.target.value
        });
    };

    const handlePolicyComponentDescriptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setPolicyComponentState({
            ...policyComponentState,
            description: event.target.value
        });
    }

    const handlePolicyComponentCategoryChange = (selectedOption: SelectOption<string>) => {
        setPolicyComponentState({
            ...policyComponentState,
            category: selectedOption.value
        });
    };

    const handlePolicyComponentStatusChange = () => {
        const newStatus = policyComponentState.status === EntityStatusTypes.ACTIVE
            ? EntityStatusTypes.INACTIVE
            : EntityStatusTypes.ACTIVE;

        setPolicyComponentState({
            ...policyComponentState,
            status: newStatus
        });
    };

    const selectedPolicyComponentCreationMode = useMemo(() => {
        const option = POLICY_COMPONENT_CREATION_TYPE_OPTIONS.find(opt => opt.value === policyComponentCreationMode);
        if (option) {
            return option;
        }

        return null;
    }, [POLICY_COMPONENT_CREATION_TYPE_OPTIONS, policyComponentCreationMode]);

    const organizationOptions = useMemo(() => {
        const options = companies.filter(company => company.type === "RP")
            .map(company => ({ label: company.name, value: company.id }))
            .sort((a, b) => sortStrings(a.label, b.label));
        return options;
    }, [companies]);

    const selectedOrganization = useMemo(() => {
        return organizationOptions.find(org => org.value === policyComponentState.organizationID) || null;
    }, [organizationOptions, policyComponentState.organizationID]);

    const selectedCategory = useMemo(() => {
        if (policyComponentState?.category) {
            return POLICY_CATEGORY_OPTIONS.find(category => category.value === policyComponentState.category) || null;
        }

        return null;

    }, [policyComponentState, POLICY_CATEGORY_OPTIONS]);

    const policyComponentOptions = useMemo(() => {
        return policyComponents.map(component => {
            return {
                label: component.name,
                value: component.id
            }
        }).sort((a, b) => sortStrings(a.label, b.label));
    }, [policyComponents]);

    const selectedPolicyComponentOption = useMemo(() => {
        if (selectedPolicyComponent) {
            return policyComponentOptions.find(option => option.value === selectedPolicyComponent.id);
        }

        return null;
    }, [policyComponentOptions, selectedPolicyComponent]);

    return (
        <div className={styles.createPolicyComponentGeneralStepContainer}>

            <span className={styles.description}>
                {createPolicyComponentResource.description}
            </span>
            <SelectFormGroup
                automationId={`policy-component-type-select`}
                controlId={`policy-component-type-select`}
                label={createPolicyComponentResource.inputs.componentCreationMode.label}
                ariaLabel={createPolicyComponentResource.inputs.componentCreationMode.label}
                onChange={handlePolicyComponentTypeChange}
                options={POLICY_COMPONENT_CREATION_TYPE_OPTIONS}
                selectedOption={selectedPolicyComponentCreationMode}
                disabled={false}
                isRequired={true}
                isSearchable={true}
                isClearable={false}
                labelWidth={labelWidth}
                layout={layout}
            />
            <hr />
            {policyComponentCreationMode === PolicyComponentCreationModeTypes.FROM_COPY_COMPONENT &&
                <><SelectFormGroup
                    automationId={`general-step-policy-component-select`}
                    controlId={`general-step-policy-component-select`}
                    label={createPolicyComponentResource.inputs.components.label}
                    ariaLabel={createPolicyComponentResource.inputs.components.label}
                    onChange={handlePolicyComponentListChange}
                    options={policyComponentOptions}
                    selectedOption={selectedPolicyComponentOption}
                    disabled={false}
                    isRequired={true}
                    isSearchable={true}
                    isClearable={false}
                    labelWidth={labelWidth}
                    layout={layout}
                />
                    <hr />
                </>
            }
            <SelectFormGroup
                automationId={`general-step-organization-select`}
                controlId={`general-step-organization-select`}
                label={createPolicyComponentResource.inputs.organization.label}
                ariaLabel={createPolicyComponentResource.inputs.organization.label}
                onChange={handleOrganizationChange}
                options={organizationOptions}
                selectedOption={selectedOrganization}
                disabled={companies.length === 1}
                isRequired={companies.length === 1}
                isSearchable={true}
                isClearable={companies.length > 1}
                labelWidth={labelWidth}
                layout={layout}
            />
            <InputFormGroup
                automationId={`policy-component-name-select`}
                controlId={`policy-component-name-select`}
                label={createPolicyComponentResource.inputs.name.label}
                ariaLabel={createPolicyComponentResource.inputs.name.label}
                value={policyComponentState.name}
                onChange={handlePolicyComponentNameChange}
                disabled={false}
                isRequired={true}
                labelWidth={labelWidth}
                layout={layout}
            />
            {validationErrors.nameError}
            <InputFormGroup
                automationId={`policy-component-description-input`}
                controlId={`policy-component-description-input`}
                label={createPolicyComponentResource.inputs.description.label}
                ariaLabel={createPolicyComponentResource.inputs.description.label}
                value={policyComponentState.description}
                onChange={handlePolicyComponentDescriptionChange}
                disabled={false}
                isRequired={true}
                labelWidth={labelWidth}
                layout={layout}
            />
            {validationErrors.descriptionError}
            <SelectFormGroup
                automationId={`policy-component-category-select`}
                controlId={`policy-component-category-select`}
                label={createPolicyComponentResource.inputs.category.label}
                ariaLabel={createPolicyComponentResource.inputs.category.label}
                onChange={handlePolicyComponentCategoryChange}
                options={POLICY_CATEGORY_OPTIONS}
                selectedOption={selectedCategory}
                disabled={false}
                isRequired={true}
                isSearchable={true}
                isClearable={false}
                labelWidth={labelWidth}
                layout={layout}
            />
            {validationErrors.categoryError}
            <ToggleFormGroup
                controlId={`policy-component-status-radio-input`}
                automationId={`policy-component-status-radio-input`}
                label={createPolicyComponentResource.inputs.status.label}
                layout={layout}
                labelWidth={labelWidth}
                onChange={handlePolicyComponentStatusChange}
                checkedIcon={false}
                uncheckedIcon={false}
                checked={policyComponentState.status === EntityStatusTypes.ACTIVE}
                value={policyComponentState.status}
                isRequired={true}
                disabled={false}
            />
        </div>
    );
}